import { useState } from "react";
import { Trash2, KeyRound, LogOut, UserX, UserCheck, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { Panel } from "@/components/rc/shared";
import { ROLE_LABEL } from "@/context/WorkspaceContext";

const ResetLink = ({ link, onClose }) => {
  const url = `${window.location.origin}${link.path}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Reset link copied");
    } catch {
      toast.error("Could not copy. Please select and copy the link.");
    }
  };
  return (
    <div role="status" className="mt-3 rounded-2xl border-2 border-line bg-surface p-4" data-testid="member-reset-link">
      <p className="font-bold">Send this one-time link to {link.name}. It works for {link.expires_minutes} minutes.</p>
      <p className="mt-2 break-all rounded-xl bg-paper px-3 py-2 font-mono text-sm">{url}</p>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={copy} className="btn btn-brand btn-sm" data-testid="member-reset-copy"><Copy size={14} aria-hidden /> Copy link</button>
        <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Done</button>
      </div>
    </div>
  );
};

const Actions = ({ m, act, onLink }) => (
  <div className="flex flex-wrap items-center gap-2">
    <label htmlFor={`role-${m.id}`} className="sr-only">Role for {m.name}</label>
    <select id={`role-${m.id}`} value={m.role} onChange={(e) => act(() => api.patch(`/rc/members/${m.id}`, { role: e.target.value }), `${m.name} is now ${ROLE_LABEL[e.target.value]}`)} className="field-input py-2" data-testid={`member-role-${m.email}`}>
      {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    <button type="button" onClick={() => onLink(m)} className="btn btn-ghost btn-sm" aria-label={`Create a password reset link for ${m.name}`} data-testid={`member-reset-${m.email}`}><KeyRound size={14} aria-hidden /> Reset link</button>
    <button type="button" onClick={() => act(() => api.post(`/rc/members/${m.id}/signout`), `${m.name} has been signed out everywhere`)} className="btn btn-ghost btn-sm" aria-label={`Sign ${m.name} out everywhere`} data-testid={`member-signout-${m.email}`}><LogOut size={14} aria-hidden /></button>
    <button type="button" onClick={() => act(() => api.patch(`/rc/members/${m.id}/active`, { active: !m.active }), m.active ? `${m.name} deactivated` : `${m.name} reactivated`)} className="btn btn-ghost btn-sm" aria-label={m.active ? `Deactivate ${m.name}` : `Reactivate ${m.name}`} data-testid={`member-active-${m.email}`}>
      {m.active ? <UserX size={14} aria-hidden /> : <UserCheck size={14} aria-hidden />}
    </button>
    <button type="button" onClick={() => window.confirm(`Delete ${m.name}'s account?`) && act(() => api.delete(`/rc/members/${m.id}`), `${m.name} removed`)} className="btn btn-ghost btn-sm" aria-label={`Delete ${m.name}`} data-testid={`member-remove-${m.email}`}><Trash2 size={14} aria-hidden /></button>
  </div>
);

export const Team = ({ members, me, manage, reload }) => {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      reload();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const onLink = async (m) => {
    try {
      const { data } = await api.post(`/rc/members/${m.id}/reset-link`);
      setLink({ ...data, id: m.id, name: m.name });
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <Panel id="ws-team" eyebrow={`${members.length} ${members.length === 1 ? "person" : "people"}`} title="Team">
      <p className="mb-5 max-w-2xl text-ink-soft">SENDCOs and Headteachers are organisation administrators. Staff can complete assessments, upload evidence and save progress. Governors can view everything and download reports, but cannot make changes.</p>
      <ul className="space-y-3" aria-busy={busy} data-testid="team-list">
        {members.map((m) => (
          <li key={m.id} className="rounded-2xl border-2 border-line bg-paper p-4" data-testid={`member-${m.email}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-bold">{m.name}{m.id === me ? " (you)" : ""}{!m.active && <span className="ml-2 rounded-full px-2 py-0.5 text-xs" style={{ background: "var(--rag-red)", color: "var(--rag-red-fg)" }}>Deactivated</span>}</p>
                <p className="text-sm text-ink-soft">{m.email}</p>
              </div>
              {manage && m.id !== me ? <Actions m={m} act={act} onLink={onLink} /> : <span className="rounded-full border-2 border-line px-3 py-1 text-sm font-bold">{ROLE_LABEL[m.role]}</span>}
            </div>
            {link?.id === m.id && <ResetLink link={link} onClose={() => setLink(null)} />}
          </li>
        ))}
      </ul>
    </Panel>
  );
};
